import React, { useContext } from 'react';
import classnames from 'classnames';
import SiteFooter from '@wix/da-shared-react/pkg/SiteFooter';
import { BiDataHook } from '@wix/da-bi/pkg/constants';
import type { BIEventPayload } from '@wix/da-bi/pkg/types';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import Header from '../../../Header';
import NetworkBarWrapper from '../NetworkBarWrapper';
import ScrollDependentStickyContainer from '../../../ScrollDependentStickyContainter';
import ddt from '@wix/da-ddt';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import TopAd from '../../../Ads/TopAd';
import BottomAd from '../../../Ads/BottomAd';
import RightAd from '../../../Ads/RightAd';
import MobileAdhesion from '@wix/da-ads/pkg/adunits/anon/MobileAdhesion';
import DesktopAdhesion from '@wix/da-ads/pkg/adunits/anon/DesktopAdhesion';
import UserMobileAdhesion from '@wix/da-ads/pkg/adunits/user/MobileAdhesion';
import AnonOrUserUnit from '@wix/da-ads/pkg/adunits/AnonOrUserUnit';
import { useAdsType } from '@wix/da-ads/pkg/useAdsType';

import s from './PageContainer.scss';

export interface Props {
  title?: React.ReactNode;
  tagLine?: React.ReactNode;
  customHeader?: React.ReactNode;
  headerNavigation?: React.ReactNode;
  headerLeftSideContent?: React.ReactNode;
  headerRightSideContent?: React.ReactNode;
  headerIsStickyOnScrollUp?: boolean;
  withNavigation?: boolean;
  withNetworkBar?: boolean;
  withTabHeader?: boolean;
  withAds?: boolean;
  withAdhesion?: boolean;
  withRightAds?: boolean;
  isSearchPage?: boolean;
  isInfiniteScroll?: boolean;
  hasMore?: boolean;
  withFooter?: boolean;
  className?: string;
  titleClassName?: string;
  biData?: BIEventPayload;
  children?: React.ReactNode;
  adSafety?: string;
}

const PageContainer: React.FC<Props> = ({
  children,
  withTabHeader = true,
  isSearchPage,
  withFooter = false,
  className,
  title,
  tagLine,
  headerNavigation,
  headerRightSideContent,
  headerLeftSideContent,
  withNetworkBar = true,
  biData: { itemid: sectionid, typeid: section_typeid, ...biData } = {},
  withNavigation,
  titleClassName,
  customHeader,
  headerIsStickyOnScrollUp = false,
  withAds,
  withAdhesion,
  withRightAds,
  adSafety,
  isInfiniteScroll,
  hasMore,
}) => {
  const isMobile = useContext(MobileContext);
  const adType = useAdsType();

  // top ad shows in desktop only
  const showTopAd = !isMobile && withAds;
  // right ad shows in desktop only
  const showRightAd = !isMobile && withAds && withRightAds;
  // bottom ad shows in desktop only, whilst in paginated mode, or at the very bottom of an infinite scrolled page.
  const showBottomAd =
    !isMobile && withAds && (!isInfiniteScroll || hasMore === false);

  if (withAds && headerIsStickyOnScrollUp) {
    ddt.error('ads', 'You cannot have ads with headerIsStickyOnScrollUp');
  }

  return (
    <NetworkBarWrapper
      isNetworkBarEnabled={withNetworkBar}
      className={classnames(
        s['root'],
        !withFooter && !withNetworkBar && s['no-footer-page'],
        showRightAd && s['with-right-rail']
      )}
    >
      {showRightAd && <RightAd />}
      <BiLoggerContextProvider value={{ sectionid, section_typeid, ...biData }}>
        {isMobile && withAdhesion && (
          <AnonOrUserUnit
            anon={MobileAdhesion}
            user={UserMobileAdhesion}
            safety={adSafety!}
          />
        )}
        {!isMobile && withAdhesion && adType === 'anon' && (
          <DesktopAdhesion safety={adSafety!} />
        )}

        {headerIsStickyOnScrollUp && (
          <ScrollDependentStickyContainer className={s['scroll-sticky']}>
            {customHeader}
            {withTabHeader && (
              <Header
                title={title}
                tagline={tagLine}
                rightSideContent={headerRightSideContent}
                leftSideContent={headerLeftSideContent}
                className={s['header']}
                withNavigation={withNavigation}
                titleClassName={titleClassName}
                isSticky={false}
              >
                {headerNavigation}
              </Header>
            )}
          </ScrollDependentStickyContainer>
        )}
        {!headerIsStickyOnScrollUp && (
          <>
            {showTopAd && <TopAd />}
            {customHeader}
            {withTabHeader && (
              <Header
                title={title}
                tagline={tagLine}
                rightSideContent={headerRightSideContent}
                leftSideContent={headerLeftSideContent}
                className={s['header']}
                withNavigation={withNavigation}
                titleClassName={titleClassName}
              >
                {headerNavigation}
              </Header>
            )}
          </>
        )}
        <div
          className={classnames(
            s['page'],
            isSearchPage && s['search'],
            withTabHeader && s['with-header']
          )}
          data-hook={BiDataHook.BROWSE_ALL_CONTENT}
        >
          <div className={className}>{children}</div>
          {showBottomAd && <BottomAd />}
        </div>
      </BiLoggerContextProvider>
      {withFooter && <SiteFooter />}
    </NetworkBarWrapper>
  );
};
PageContainer.displayName = 'PageContainer';

export default PageContainer;
